/* eslint-disable jsx-a11y/img-redundant-alt */
import {
    Angry,
    ImageOff,
    Mars,
    Phone,
    Share,
    Trash2,
    UploadCloudIcon,
    Venus,
    X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    calculateAge,
    extractTimestamp,
    formatFirestoreTimestamp,
    isMac,
} from "../utils/common";
import WeightClassPicker from "../components/weight-class-picker";
import WeightPropagateModal from "../components/weight-propagate-modal";
import {
    authIdentifierNames,
    fighterEnties,
    fighterEntryDisplayMapping,
    monthMap,
    months,
    nationalities,
} from "../constants";
import SelectInput from "../components/select-input";
import { Timestamp } from "firebase/firestore";
import { FaGoogle, FaInstagram, FaWhatsapp, FaYoutube } from "react-icons/fa";
import {
    applyWeightToRegistrations,
    createFighter,
    deleteFighter,
    fetchLiveRegistrationsForFighter,
    updateFighter,
    uploadFighterProfileImage,
} from "../utils/firebase";
import toast from "react-hot-toast";

export default function ManageFighter({ fighter, fighters, handleClose }) {
    const [fighterInfo, setFighterInfo] = useState(fighter);
    const [changed, setChanged] = useState(false);
    // The class this fighter had when the editor opened — what the picker
    // compares against and what decides whether the save needs to ask about
    // registrations.
    const [profileBaselineClass] = useState(
        () => fighter?.weightClass ?? null
    );
    const [weightPrompt, setWeightPrompt] = useState(null);
    const [selectedRegIds, setSelectedRegIds] = useState(() => new Set());
    const [propagating, setPropagating] = useState(false);
    const [nationalityDropdownOpen, setNationalityDropDownOpen] =
        useState(false);
    const containerRef = useRef(null);
    const profileImageInputRef = useRef(null);
    const [profileImageInputDragActive, setProfileImageInputDragActive] =
        useState(false);
    const [newProfileImage, setNewProfileImage] = useState(null);
    const [newProfileImageURL, setNewProfileImageURL] = useState(null);

    const shareUrl = `${window.location.origin}/fighter/${fighter.id}`;

    const handleChange = (e) => {
        const name = e.target.name;
        const value = e.target.value;

        if (name === "fighterPicture") {
            if (e.target.files && e.target.files[0]) {
                const file = e.target.files[0];

                setNewProfileImage(file);
            }
            return;
        }

        if (!changed) {
            setChanged(true);
        }

        if (name === "nationality") {
            setNationalityDropDownOpen(true);
        }

        switch (name) {
            case "birthdate-year":
                const year = parseInt(value);
                if (!!year && year > 0 && year <= 9999) {
                    setFighterInfo((prev) => {
                        const d = prev?.birthdate?.toDate() ?? new Date();
                        d.setFullYear(year);

                        const a = calculateAge(d);

                        return {
                            ...prev,
                            age: a,
                            birthdate: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "birthdate-month":
                const month = monthMap[value];
                if (month !== undefined) {
                    setFighterInfo((prev) => {
                        const d = prev.birthdate.toDate();
                        d.setMonth(month);

                        const a = calculateAge(d);

                        return {
                            ...prev,
                            age: a,
                            birthdate: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "birthdate-date":
                const date = parseInt(value);
                if (!!date && date > 0 && date <= 31) {
                    setFighterInfo((prev) => {
                        const d = prev.birthdate.toDate();
                        d.setDate(date);

                        const a = calculateAge(d);

                        return {
                            ...prev,
                            age: a,
                            birthdate: Timestamp.fromDate(d),
                        };
                    });
                }
                break;
            case "age":
                setFighterInfo((prev) => {
                    const now = new Date();
                    const newYear = now.getFullYear() - value;

                    const d = prev?.birthdate?.toDate() ?? new Date();
                    d.setFullYear(newYear);

                    return {
                        ...prev,
                        age: value,
                        birthdate: Timestamp.fromDate(d),
                    };
                });
                break;
            default:
                setFighterInfo((prev) => {
                    return {
                        ...prev,
                        [name]: value,
                    };
                });
                break;
        }
    };

    const handleSelectNationality = (nation) => {
        setFighterInfo((prev) => {
            return {
                ...prev,
                nationality: nation,
            };
        });
        setNationalityDropDownOpen(false);
    };

    const handleRecordChange = (entry, key, value) => {
        if (!changed) {
            setChanged(true);
        }

        setFighterInfo((prev) => {
            return {
                ...prev,
                records: {
                    ...prev.records,
                    [entry]: {
                        ...prev.records[entry],
                        [key]: (parseInt(value) ?? 0) > 0 ? parseInt(value) : 0,
                    },
                },
            };
        });
    };

    /** Write the profile, then optionally push the weight onto registrations. */
    const saveProfile = useCallback(
        async (registrationIds = []) => {
            await updateFighter(fighter.id, fighterInfo);
            if (registrationIds.length > 0) {
                await applyWeightToRegistrations(
                    fighter.id,
                    registrationIds,
                    fighterInfo?.weightClass
                );
            }
            handleClose(fighterInfo);
        },
        [fighter.id, fighterInfo, handleClose]
    );

    const handleSubmit = useCallback(async () => {
        try {
            if (!!fighter.id) {
                // A weight edit on a fighter who is sitting in live events has
                // consequences beyond the profile — ask before writing, and
                // show which bouts it would disturb.
                const nextClass = fighterInfo?.weightClass || null;
                const weightMoved =
                    !!nextClass && nextClass !== profileBaselineClass;

                if (weightMoved) {
                    const registrations =
                        await fetchLiveRegistrationsForFighter(fighter.id);
                    if (registrations.length > 0) {
                        // Unmatched registrations are safe to move, so they
                        // start ticked. Matched ones never do.
                        setSelectedRegIds(
                            new Set(
                                registrations
                                    .filter((r) => !r.matchWith)
                                    .map((r) => r.id)
                            )
                        );
                        setWeightPrompt({
                            registrations,
                            previousClass: profileBaselineClass,
                            nextClass,
                        });
                        return;
                    }
                }

                toast.promise(saveProfile(), {
                    success: () => "Updated",
                    error: (error) => {
                        console.error("Error on handleSubmit(): ", error);
                        return `Something went wrong when Update Fighter Info`;
                    },
                    loading: "Updating",
                });
            } else {
                toast.promise(createFighter(fighterInfo), {
                    success: (newFighterInfo) => {
                        handleClose(fighterInfo);
                    },
                    error: (error) => {
                        console.error("Error on handleSubmit(): ", error);
                        return `Something went wrong when Update Fighter Info`;
                    },
                    loading: "Updating",
                });
            }
        } catch (error) {
            console.error(error);
        }
    }, [
        fighter.id,
        fighterInfo,
        handleClose,
        profileBaselineClass,
        saveProfile,
    ]);

    /** Commit the save the weight prompt was holding up. */
    const handleWeightPromptApply = useCallback(
        async (ids) => {
            setPropagating(true);
            try {
                await saveProfile(ids);
                setWeightPrompt(null);
                toast.success(
                    ids.length
                        ? `Updated profile and ${ids.length} registration${
                              ids.length === 1 ? "" : "s"
                          }.`
                        : "Updated profile."
                );
            } catch (error) {
                console.error("Error applying weight: ", error);
                toast.error("Something went wrong when saving the weight.");
            } finally {
                setPropagating(false);
            }
        },
        [saveProfile]
    );

    // Handle drag events to toggle dragActive state.
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setProfileImageInputDragActive(true);
        } else if (e.type === "dragleave") {
            setProfileImageInputDragActive(false);
        }
    };

    // Handle file drop.
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setProfileImageInputDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            setNewProfileImage(file);
        }
    };

    const handleUploadFighterPicture = async () => {
        try {
            if (!!newProfileImage) {
                const downloadURL = await uploadFighterProfileImage(
                    newProfileImage,
                    fighterInfo.id
                );
                setNewProfileImage(null);
                setFighterInfo((prev) => {
                    return {
                        ...prev,
                        fighterPicture: downloadURL,
                    };
                });
                setNewProfileImageURL(downloadURL);
                toast.success("New Fighter Image Uploaded.");
            }
        } catch (error) {
            console.error(error);
            toast.error(
                "Something went wrong when uploading new Fighter Picture."
            );
        }
    };

    const filteredNationalities = nationalities.filter((n) => {
        return n
            .toLowerCase()
            .includes(fighterInfo.nationality?.toLowerCase() ?? "");
    });

    const nameInput = (
        <input
            type="text"
            id="name"
            className="input input-bordered w-full"
            placeholder="name"
            value={fighterInfo?.name ?? ""}
            name="name"
            onChange={handleChange}
        />
    );

    const emailInput = (
        <input
            type="text"
            id="email"
            className="input input-bordered w-full"
            placeholder="Email"
            value={fighterInfo?.email ?? ""}
            name="email"
            onChange={handleChange}
        />
    );

    const recordsInput = (
        <div className="flex flex-center w-full gap-2">
            {fighterEnties.map((entry, index) => {
                return (
                    <div
                        key={index}
                        className="flex flex-row w-full items-center justify-start gap-2"
                    >
                        <p className="w-full flex-2">
                            {fighterEntryDisplayMapping[entry]}:
                        </p>
                        <input
                            className="flex-1 min-w-24 input input-bordered"
                            min={0}
                            placeholder="WIN"
                            type="number"
                            defaultValue={
                                fighterInfo?.records?.[entry]?.win ?? 0
                            }
                            name="win"
                            id="win"
                            onChange={(e) => {
                                handleRecordChange(
                                    entry,
                                    e.target.name,
                                    e.target.value
                                );
                            }}
                        />
                        <input
                            className="flex-1 min-w-24 input input-bordered"
                            min={0}
                            placeholder="LOSS"
                            type="number"
                            defaultValue={
                                fighterInfo?.records?.[entry]?.loss ?? 0
                            }
                            name="loss"
                            id="loss"
                            onChange={(e) => {
                                handleRecordChange(
                                    entry,
                                    e.target.name,
                                    e.target.value
                                );
                            }}
                        />
                        <input
                            className="flex-1 min-w-24 input input-bordered"
                            min={0}
                            placeholder="DRAW"
                            type="number"
                            defaultValue={
                                fighterInfo?.records?.[entry]?.draw ?? 0
                            }
                            name="draw"
                            id="draw"
                            onChange={(e) => {
                                handleRecordChange(
                                    entry,
                                    e.target.name,
                                    e.target.value
                                );
                            }}
                        />
                    </div>
                );
            })}
            <div className="divider m-0" />
            <div className="flex w-full justify-between items-center opacity-70">
                <p className="w-full">Totals</p>
                <p className="min-w-24">
                    {fighterEnties
                        .map((entry) => {
                            return fighterInfo?.records?.[entry]?.win ?? 0;
                        })
                        .reduce((a, b) => {
                            return a + (parseInt(b) || 0);
                        }, 0)}
                </p>
                <p className="min-w-24">
                    {fighterEnties
                        .map((entry) => {
                            return fighterInfo?.records?.[entry]?.loss ?? 0;
                        })
                        .reduce((a, b) => {
                            return a + (parseInt(b) || 0);
                        }, 0)}
                </p>
                <p className="min-w-24">
                    {fighterEnties
                        .map((entry) => {
                            return fighterInfo?.records?.[entry]?.draw ?? 0;
                        })
                        .reduce((a, b) => {
                            return a + (parseInt(b) || 0);
                        }, 0)}
                </p>
            </div>
        </div>
    );

    const ageInput = (
        <input
            className="w-full input input-bordered"
            placeholder="Age"
            type="number"
            min={0}
            value={fighterInfo?.age ?? 0}
            name="age"
            id="age"
            onChange={handleChange}
        />
    );

    const genderInput = (
        <div className="border flex flex-row w-full gap-4 p-2">
            <button
                className={
                    "flex-1 btn btn-sm " +
                    (fighterInfo?.gender?.toUpperCase() === "M"
                        ? "bg-blue-300 hover:bg-blue-400 text-base-100"
                        : "")
                }
                onClick={() => {
                    handleChange({
                        target: {
                            name: "gender",
                            value: "M",
                        },
                    });
                }}
            >
                <Mars />
            </button>
            <button
                className={
                    "flex-1 btn btn-sm " +
                    (fighterInfo?.gender?.toUpperCase() === "F"
                        ? "bg-pink-300 hover:bg-pink-400 text-base-100"
                        : "")
                }
                onClick={() => {
                    handleChange({
                        target: {
                            name: "gender",
                            value: "F",
                        },
                    });
                }}
            >
                <Venus />
            </button>
        </div>
    );

    const weightClassInput = (
        <div className="w-full border border-base-300 p-4">
            <WeightClassPicker
                value={fighterInfo?.weightClass ?? ""}
                baselineClass={profileBaselineClass}
                onChange={(weightClass) => {
                    if (!changed) setChanged(true);
                    setFighterInfo((prev) => ({ ...prev, weightClass }));
                }}
            />
        </div>
    );

    const heightInput = (
        <input
            className="w-full input input-bordered"
            placeholder="Height"
            type="number"
            defaultValue={fighterInfo?.height ?? 0}
            name="height"
            id="height"
            min={0}
            onChange={handleChange}
        />
    );

    const nationalityInput = (
        <SelectInput
            options={nationalities}
            name="nationality"
            placeholder="Nationality..."
            onSelect={(n) => {
                setFighterInfo((prev) => {
                    return {
                        ...prev,
                        nationality: n,
                    };
                });
            }}
            onChange={handleChange}
            defaultValue={fighterInfo?.nationality}
            value={fighterInfo?.nationality}
        />
    );

    const birthdateInput = (
        <div className="w-full flex flex-row items-center justify-center gap-2">
            <input
                className="w-full input input-bordered"
                placeholder="Date"
                type="number"
                defaultValue={
                    extractTimestamp(fighterInfo?.birthdate)?.day ?? 1
                }
                name="birthdate-date"
                id="birthdate-date"
                min={1}
                max={31}
                onChange={handleChange}
            />
            <SelectInput
                options={months}
                placeholder="Month"
                onChange={handleChange}
                name="birthdate-month"
                onSelect={(m) => {
                    setFighterInfo((prev) => {
                        const birthDate = prev.birthdate.toDate();
                        const monthIndex = monthMap[m];
                        if (!!monthMap) {
                            birthDate.setMonth(monthIndex);
                            return {
                                ...prev,
                                birthdate: Timestamp.fromDate(birthDate),
                            };
                        }
                    });
                }}
                defaultValue={
                    extractTimestamp(fighterInfo?.birthdate)?.month ?? ""
                }
                value={extractTimestamp(fighterInfo?.birthdate)?.month ?? ""}
            />
            <input
                className="w-full input input-bordered"
                placeholder="Year"
                type="number"
                value={extractTimestamp(fighterInfo?.birthdate)?.year ?? ""}
                name="birthdate-year"
                id="birthdate-year"
                min={1}
                max={9999}
                onChange={handleChange}
            />
        </div>
    );

    const createdAtInput = (
        <input
            className="w-full input input-bordered"
            placeholder="createdAt"
            type="text"
            defaultValue={
                formatFirestoreTimestamp(fighterInfo?.createdAt) ??
                "Not Available"
            }
            readOnly
            disabled
        />
    );
    const updateAtInput = (
        <input
            className="w-full input input-bordered"
            placeholder="updateAt"
            type="text"
            defaultValue={
                formatFirestoreTimestamp(fighterInfo?.updateAt) ??
                "Not Available"
            }
            readOnly
            disabled
        />
    );

    const phoneNumberInput = (
        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
            <input
                className="w-full px-4"
                placeholder="phoneNumber"
                type="text"
                defaultValue={fighterInfo?.phoneNumber ?? 0}
                name="phoneNumber"
                id="phoneNumber"
                onChange={handleChange}
            />
            <a
                className="btn btn-ghost btn-square"
                target="_blank"
                href={`https://web.whatsapp.com/send/?phone=${fighterInfo?.phoneNumber
                    ?.replaceAll("+", "")
                    .replaceAll("-", "")
                    .replaceAll("-", "")}`}
                rel="noreferrer"
            >
                <FaWhatsapp size={18} />
            </a>
        </div>
    );

    const emergencyContactNumberInput = (
        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
            <input
                className="w-full px-4"
                placeholder="emergencyContactNumber"
                type="text"
                defaultValue={fighterInfo?.emergencyContactNumber ?? 0}
                name="emergencyContactNumber"
                id="emergencyContactNumber"
                onChange={handleChange}
            />
            <a
                className="btn btn-ghost btn-square"
                target="_blank"
                href={`tel:${fighterInfo?.emergencyContactNumber}`}
                rel="noreferrer"
            >
                <Phone className="h-4" />
            </a>
        </div>
    );

    const icOrPassportInput = (
        <input
            className="w-full input input-bordered"
            placeholder="icOrPassport"
            type="text"
            defaultValue={fighterInfo?.icOrPassport ?? 0}
            name="icOrPassport"
            id="icOrPassport"
            onChange={handleChange}
        />
    );

    const gymNameOrTeamNameInput = (
        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
            <input
                className="w-full px-4"
                placeholder="gymNameOrTeamName"
                type="text"
                defaultValue={fighterInfo?.gymNameOrTeamName ?? 0}
                name="gymNameOrTeamName"
                id="gymNameOrTeamName"
                onChange={handleChange}
            />
            <a
                className="btn btn-ghost btn-square"
                target="_blank"
                href={`https://www.google.com/search?q=${fighterInfo?.gymNameOrTeamName}`}
                rel="noreferrer"
            >
                <FaGoogle size={18} />
            </a>
        </div>
    );

    const walkOutMusicInput = (
        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
            <input
                className="w-full px-4"
                placeholder="walkOutMusic"
                type="text"
                defaultValue={fighterInfo?.walkOutMusic ?? 0}
                name="walkOutMusic"
                id="walkOutMusic"
                onChange={handleChange}
            />
            <a
                className="btn btn-ghost btn-square"
                target="_blank"
                href={`https://www.youtube.com/results?search_query=${fighterInfo?.walkOutMusic}`}
                rel="noreferrer"
            >
                <FaYoutube size={18} />
            </a>
        </div>
    );

    const igUsernameInput = (
        <div className="input input-bordered flex items-center gap-2 w-full relative p-0">
            <input
                className="w-full px-4"
                placeholder="igUsername"
                type="text"
                defaultValue={fighterInfo?.igUsername ?? 0}
                name="igUsername"
                id="igUsername"
                onChange={handleChange}
            />
            <a
                className="btn btn-ghost btn-square"
                target="_blank"
                href={`https://www.instagram.com/${fighterInfo?.igUsername?.replace(
                    "@",
                    ""
                )}`}
                rel="noreferrer"
            >
                <FaInstagram size={18} />
            </a>
        </div>
    );

    const tkoInput = (
        <input
            className="w-full input input-bordered"
            placeholder="tko"
            type="number"
            defaultValue={fighterInfo?.tko ?? 0}
            name="tko"
            id="tko"
            onChange={handleChange}
        />
    );

    const koInput = (
        <input
            className="w-full input input-bordered"
            placeholder="ko"
            type="number"
            defaultValue={fighterInfo?.ko ?? 0}
            name="ko"
            id="ko"
            onChange={handleChange}
        />
    );

    const submissionInput = (
        <input
            className="w-full input input-bordered"
            placeholder="submission"
            type="number"
            defaultValue={fighterInfo?.submission ?? 0}
            name="submission"
            id="submission"
            onChange={handleChange}
        />
    );

    const decisionInput = (
        <input
            className="w-full input input-bordered"
            placeholder="decision"
            type="number"
            defaultValue={fighterInfo?.decision ?? 0}
            name="decision"
            id="decision"
            onChange={handleChange}
        />
    );

    // To listen when ESC keydown then unfocus the input or close the window.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                const activeElement = document.activeElement;
                if (
                    activeElement &&
                    ["input", "textarea", "select"].includes(
                        activeElement.tagName.toLowerCase()
                    )
                ) {
                    activeElement.blur();
                    setNationalityDropDownOpen(false);
                } else {
                    handleClose(null, newProfileImageURL);
                }
            }

            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                if (changed) {
                    event.preventDefault();
                    handleSubmit();
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleClose, handleSubmit, changed]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setNationalityDropDownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="flex-center justify-start py-4 px-6 flex-1 md:border-l-2 border-current gap-4 w-full">
            <div className="w-full flex flex-row items-center gap-2">
                <Angry />
                <p className="font-bold text-xl">Fighter</p>
                <div className="flex-1" />
                <button
                    className="btn btn-ghost btn-square btn-sm"
                    onClick={() => {
                        navigator.clipboard.writeText(shareUrl);
                        toast("📋 Copied Fighter Profile URL to Clipboard");
                    }}
                >
                    <Share size={20} />
                </button>
                {!!fighter.id && (
                    <button
                        className="btn btn-ghost btn-square btn-sm btn-error"
                        onClick={() => {
                            const dialog = document.getElementById(
                                "delete-fighter-dialog"
                            );
                            dialog.showModal();
                        }}
                        title="Delete Fighter"
                    >
                        <Trash2 size={20} className="text-red-500" />
                    </button>
                )}
                <button
                    className="btn btn-outline btn-square btn-sm"
                    onClick={() => handleClose(null, newProfileImageURL)}
                >
                    <X />
                </button>
            </div>
            {/* Fighter Profile Image */}
            <div className="relative w-full flex flex-row gap-4">
                {!!newProfileImage ? (
                    <div className="relative flex-center aspect-square w-36 h-36">
                        <img
                            className="w-36 h-36 border p-1 object-cover object-center"
                            src={URL.createObjectURL(newProfileImage)}
                            alt={`${fighter.name} New Picture`}
                        />
                        <button
                            className="btn btn-circle btn-sm btn-outline absolute bottom-0 right-0 translate-x-1/3 translate-y-1/3 bg-base-100"
                            onClick={handleUploadFighterPicture}
                        >
                            <UploadCloudIcon />
                        </button>
                    </div>
                ) : !!fighterInfo?.fighterPicture ? (
                    <img
                        className="min-w-36 h-36 border p-1 object-cover object-center"
                        src={fighterInfo?.fighterPicture}
                        alt={`${fighterInfo?.name} Picture`}
                    />
                ) : (
                    <div className="w-36 h-36 aspect-square">
                        <ImageOff className="w-36 h-36 aspect-square font-3xl border p-8" />
                    </div>
                )}
                <div
                    className={
                        "w-full border-4 border-dotted flex-center h-36 flex-center hover:bg-base-200 cursor-pointer" +
                        (profileImageInputDragActive ? " !bg-base-300 " : "")
                    }
                    onClick={() => profileImageInputRef?.current?.click()}
                    onDragEnter={handleDrag}
                    onDragOver={handleDrag}
                    onDragLeave={handleDrag}
                    onDrop={handleDrop}
                >
                    <p>
                        {!profileImageInputDragActive
                            ? "Drop / Select a new Image."
                            : "Drop it!"}
                    </p>
                    <span className="text-sm">{"[PNG, JPEG, JPG]"}</span>
                    <input
                        onChange={handleChange}
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        name="fighterPicture"
                        id="fighterPicture"
                        ref={profileImageInputRef}
                        className="hidden"
                    />
                </div>
            </div>

            {/* Auth */}
            <label className="w-full font-xs opacity-60" htmlFor="auth">
                Authenticated by{" "}
                {authIdentifierNames[fighterInfo?.user?.authMethod]}:
            </label>
            <p className="input input-disabled w-full flex-center items-start">
                {fighterInfo?.user?.authIdentifier}
            </p>
            <div className="h-2" />

            {/* Email */}
            <label className="w-full font-xs opacity-60" htmlFor="email">
                Email:
            </label>
            {emailInput}
            <div className="h-2" />

            {/* Name */}
            <label className="w-full font-xs opacity-60" htmlFor="name">
                Name:
            </label>
            {nameInput}
            <div className="h-2" />

            {/* Age */}
            <label className="w-full font-xs opacity-60" htmlFor="age">
                Age:
            </label>
            {ageInput}
            <div className="h-2" />

            {/* Age */}
            <label className="w-full font-xs opacity-60" htmlFor="age">
                Birthdate:
            </label>
            {birthdateInput}
            <div className="h-2" />

            {/* Gender */}
            <label className="w-full font-xs opacity-60" htmlFor="gender">
                Gender:
            </label>
            {genderInput}
            <div className="h-2" />

            {/* Weight */}
            <label className="w-full font-xs opacity-60" htmlFor="weightClass">
                Weight Class:
            </label>
            {weightClassInput}
            <div className="h-2" />

            {/* Height */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"Height (cm):"}
            </label>
            {heightInput}
            <div className="h-2" />

            {/* Nationality */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"Nationality:"}
            </label>
            {nationalityInput}
            <div className="h-2" />

            {/* Record */}
            <label className="w-full font-xs opacity-60">
                Records (W-L-D):
            </label>
            {recordsInput}
            <div className="h-2" />

            {/* TKO */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"TKO:"}
            </label>
            {tkoInput}
            <div className="h-2" />

            {/* KO */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"KO:"}
            </label>
            {koInput}
            <div className="h-2" />

            {/* Submission */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"Submission:"}
            </label>
            {submissionInput}
            <div className="h-2" />

            {/* Decision */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"Decision:"}
            </label>
            {decisionInput}
            <div className="h-2" />

            {/* IC Passport */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"IC/Passport:"}
            </label>
            {icOrPassportInput}
            <div className="h-2" />

            {/* Phone Number */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"Phone Number:"}
            </label>
            {phoneNumberInput}
            <div className="h-2" />

            {/* Emergency Contact Number */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"Emergency Contact Number:"}
            </label>
            {emergencyContactNumberInput}
            <div className="h-2" />

            {/* Gym Name / Team Name */}
            <label className="w-full font-xs opacity-60" htmlFor="height">
                {"Gym Name / Team Name:"}
            </label>
            {gymNameOrTeamNameInput}
            <div className="h-2" />

            {/* Walk Out Music */}
            <label
                className="w-full flex flex-row items-end justify-between font-xs opacity-60"
                htmlFor="height"
            >
                <span>{"Walk Out Music:"}</span>
                <span className="text-sm">{"[Song Name - Author]"}</span>
            </label>
            {walkOutMusicInput}
            <div className="h-2" />

            {/* Height */}
            <label
                className="w-full flex flex-row items-end justify-between font-xs opacity-60"
                htmlFor="height"
            >
                <span>{"IG Username:"}</span>
                <span className="text-sm">{"[@username]"}</span>
            </label>
            {igUsernameInput}
            <div className="h-2" />

            {/* Created At */}
            <label className="w-full font-xs opacity-60">Created On:</label>
            {createdAtInput}
            <div className="h-2" />

            {/* Update At */}
            <label className="w-full font-xs opacity-60">Last Update:</label>
            {updateAtInput}
            <div className="h-2" />

            {/* Submit Button */}
            <button
                className={
                    "btn btn-primary w-full      disabled:!bg-gray-400 flex flex-row gap-4 "
                }
                disabled={!changed}
                onClick={handleSubmit}
            >
                <p>Save</p>
                {changed ? (
                    <span>
                        <kbd className="kbd kbd-xs">
                            {isMac() ? "Cmd" : "Ctrl"}
                        </kbd>{" "}
                        + <kbd className="kbd kbd-xs">Enter</kbd>
                    </span>
                ) : null}
            </button>

            {/* Delete Fighter Confirmation Modal */}
            <dialog id="delete-fighter-dialog" className="modal">
                <div className="modal-box border">
                    <h3 className="font-bold text-lg">
                        Confirm Delete Fighter
                    </h3>
                    <p className="py-4">
                        Are you sure you want to delete{" "}
                        <strong>{fighterInfo?.name}</strong>?
                    </p>
                    <div className="alert alert-error font-bold">
                        <div>
                            <ul className="list-disc list-inside text-sm space-y-1">
                                <li>
                                    This fighter will be permanently hidden from
                                    the system
                                </li>
                                <li>
                                    This fighter will not appear in any searches
                                    or listings
                                </li>
                                <li>This action cannot be undone</li>
                                <li>
                                    If the fighter is registered to events, they
                                    will remain in those events but be hidden
                                    elsewhere
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="modal-action">
                        <form method="dialog">
                            <button className="btn btn-ghost mr-2">
                                Cancel
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={async () => {
                                    const dialog = document.getElementById(
                                        "delete-fighter-dialog"
                                    );
                                    dialog.close();
                                    await toast.promise(
                                        deleteFighter({
                                            fighterId: fighter.id,
                                        }),
                                        {
                                            loading: "Deleting fighter...",
                                            success: () => {
                                                handleClose(null, null, true); // Pass deletion flag
                                                return "Fighter deleted successfully";
                                            },
                                            error: "Failed to delete fighter",
                                        }
                                    );
                                }}
                            >
                                Delete Fighter
                            </button>
                        </form>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>

            <WeightPropagateModal
                open={!!weightPrompt}
                fighterName={fighterInfo?.name}
                previousClass={weightPrompt?.previousClass ?? null}
                nextClass={weightPrompt?.nextClass ?? null}
                registrations={weightPrompt?.registrations ?? []}
                selectedIds={selectedRegIds}
                saving={propagating}
                onToggle={(id) =>
                    setSelectedRegIds((prev) => {
                        const next = new Set(prev);
                        next.has(id) ? next.delete(id) : next.add(id);
                        return next;
                    })
                }
                onProfileOnly={() => handleWeightPromptApply([])}
                onApply={() =>
                    handleWeightPromptApply(
                        (weightPrompt?.registrations ?? [])
                            .filter((r) => selectedRegIds.has(r.id))
                            .map((r) => r.id)
                    )
                }
                onCancel={() => setWeightPrompt(null)}
            />
        </div>
    );
}
